import { useCallback, useEffect, useRef, useState } from 'react';
import { v2Validate, DivisionMembership } from 'src/store/modules/auth/v2Operations';
import {
  getDivisions,
  getCurrentDivisionId,
  saveDivisions,
  saveCurrentDivisionId,
  CURRENT_DIVISION_KEY
} from 'src/utils/divisionStorage';

const DISMISSED_MISMATCH_KEY = 'sl-division-mismatch-dismissed';

function isDismissedMismatch(divisionId: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_MISMATCH_KEY) === divisionId; } catch { return false; }
}

function setDismissedMismatch(divisionId: string): void {
  try { sessionStorage.setItem(DISMISSED_MISMATCH_KEY, divisionId); } catch { /* ignore */ }
}

export function clearDismissedMismatch(): void {
  try { sessionStorage.removeItem(DISMISSED_MISMATCH_KEY); } catch { /* ignore */ }
}

interface UseDivisionMismatchReturn {
  hasMismatch: boolean;
  mismatchDivisionId: string | null;
  mismatchDivisionName: string | null;
  divisions: DivisionMembership[] | null;
  activeDivisionId: string | null;
  dismissMismatch: () => void;
}

/**
 * Detects cross-tab division changes via localStorage storage events
 * and visibilitychange + v2Validate. Shared between AccountDivisionPopover
 * and Omni's sidebar/profile UI.
 */
export function useDivisionMismatch(currentDivisionId: string | null | undefined): UseDivisionMismatchReturn {
  const [hasMismatch, setHasMismatch] = useState(false);
  const [mismatchDivisionId, setMismatchDivisionId] = useState<string | null>(null);
  const [mismatchDivisionName, setMismatchDivisionName] = useState<string | null>(null);
  const [divisions, setDivisions] = useState<DivisionMembership[] | null>(() => getDivisions());
  const [activeDivisionId, setActiveDivisionId] = useState<string | null>(() => getCurrentDivisionId());
  const detectedRef = useRef(false);

  useEffect(() => {
    const fetchDivisions = async () => {
      try {
        const response = await v2Validate();
        if (response.authenticated && response.divisions?.length > 0) {
          saveDivisions(response.divisions);
          setDivisions(response.divisions);
          if (response.division?.id) {
            setActiveDivisionId(response.division.id);
          }
        }
      } catch (error) {
        console.error('[useDivisionMismatch] Failed to fetch divisions:', error);
      }
    };
    fetchDivisions();
  }, []);

  useEffect(() => {
    const effectiveDivisionId = currentDivisionId || activeDivisionId;

    const triggerMismatch = (newId: string, newName: string) => {
      setHasMismatch(true);
      setMismatchDivisionId(newId);
      setMismatchDivisionName(newName);
      detectedRef.current = true;
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== CURRENT_DIVISION_KEY) return;
      const newDivisionId = e.newValue;
      if (newDivisionId && newDivisionId !== effectiveDivisionId && !isDismissedMismatch(newDivisionId)) {
        const cached = getDivisions();
        const newDiv = cached?.find((d) => d.id === newDivisionId);
        triggerMismatch(newDivisionId, newDiv?.name || newDivisionId);
      }
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!effectiveDivisionId) {
        return;
      }
      try {
        const response = await v2Validate();
        if (!response.authenticated || !response.division) return;
        if (response.division.id !== effectiveDivisionId && !isDismissedMismatch(response.division.id)) {
          triggerMismatch(response.division.id, response.division.name || response.division.id);
          saveDivisions(response.divisions || []);
        }
      } catch (err) {
        console.error('[useDivisionMismatch] validate error:', err);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentDivisionId, activeDivisionId]);

  const dismissMismatch = useCallback(() => {
    if (mismatchDivisionId) {
      setDismissedMismatch(mismatchDivisionId);
    }
    setHasMismatch(false);
    setMismatchDivisionId(null);
    setMismatchDivisionName(null);
  }, [mismatchDivisionId]);

  return {
    hasMismatch,
    mismatchDivisionId,
    mismatchDivisionName,
    divisions,
    activeDivisionId,
    dismissMismatch
  };
}

export default useDivisionMismatch;
